import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../../../hooks/admin/useUsers';

interface UserTableProps {
  users: User[];
}

interface WarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  userName: string;
  userStatus: string;
}

const WarningModal: React.FC<WarningModalProps> = ({ isOpen, onClose, userName, userStatus }) => {
  if (!isOpen) return null;

  const getStatusText = (status: string): string => {
    switch (status) {
      case 'inactive': return '비활성';
      case 'suspended': return '일시 정지';
      case 'blocked': return '차단됨';
      default: return status;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-sm outline outline-1 outline-slate-200 p-8 max-w-md w-full mx-4">
        <h3 className="text-[#1e2c49] text-xl font-bold font-['Pretendard'] leading-9 mb-4">접근 제한</h3>
        <div className="w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6"></div>
        <p className="text-[#6b7684] text-lg font-medium font-['Pretendard'] mb-6">
          <span className="text-[#1e2c49] font-semibold">{userName}</span> 사용자는 현재 상태가 
          <span className="text-red-600 font-semibold"> 비활성</span>입니다.
          <br /><br />
          상세 정보에 접근할 수 없습니다.
        </p>
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="w-[100px] h-11 bg-[#155acb] text-white text-base font-bold font-['Pretendard'] rounded-sm hover:bg-[#155acb]/90 transition-colors"
          >
            확인
          </button>
        </div>
      </div>
    </div>
  );
};

const UserTable: React.FC<UserTableProps> = ({ users }) => {
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // 테이블 헤더 정의
  const tableHeaders = [
    { id: 'name', label: '이름' },
    { id: 'email', label: '이메일' },
    { id: 'organization', label: '소속' },
    { id: 'phoneNumber', label: '전화번호' },
    { id: 'role', label: '권한' },
    { id: 'status', label: '상태' },
    { id: 'createdAt', label: '가입일' },
    { id: 'lastLoginAt', label: '마지막 로그인' }
  ];

  // 권한 레벨에 따른 한글 표시
  const getRoleLabel = (userLevel: number): string => {
    switch (userLevel) {
      case 1:
        return '최고 관리자';
      case 2:
        return '일반 관리자';
      case 3:
        return '공사 관계자';
      case 4:
        return '센터 관계자';
      case 5:
        return '입주업체';
      case 6:
        return '입주협력업체';
      case 7:
        return '공공기관';
      case 8:
        return '항만근로자';
      case 9:
        return '일반 사용자';
      default:
        return '일반 사용자';
    }
  };

  // 상태 텍스트 표시
  const getStatusText = (status: string): string => {
    switch (status) {
      case 'active':
        return '활성';
      case 'inactive':
        return '비활성';
      case 'suspended':
        return '일시 정지';
      case 'blocked':
        return '차단됨';
      default:
        return status;
    }
  };

  // 상태 표시를 위한 배지 클래스
  const getStatusBadgeClass = (status: string): string => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'suspended':
        return 'bg-yellow-100 text-yellow-800';
      case 'blocked':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // 날짜 형식 변환
  const formatDate = (dateString: string): string => {
    if (!dateString || dateString === 'Invalid Date') return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 마지막 로그인 표시용 특별 포맷
  const formatLastLogin = (lastLoginAt: string, createdAt: string): string => {
    if (!lastLoginAt || lastLoginAt === 'Invalid Date') return formatDate(createdAt);
    const date = new Date(lastLoginAt);
    if (isNaN(date.getTime())) return formatDate(createdAt);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 사용자 클릭 핸들러
  const handleUserClick = (user: User) => {
    if (user.status === 'active') {
      navigate(`/admin/users/${user.uid}`);
    } else {
      setSelectedUser(user);
      setShowWarning(true);
    }
  };

  // 경고 모달 닫기
  const handleCloseWarning = () => {
    setShowWarning(false);
    setSelectedUser(null);
  };

  if (users.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-[#6b7684] text-lg font-medium font-['Pretendard']">사용자 데이터가 없습니다.</p>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="bg-white rounded-sm outline outline-1 outline-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[#e5e8eb]">
            <thead className="bg-[#f8fafc]">
              <tr>
                {tableHeaders.map((header) => (
                  <th 
                    key={header.id}
                    scope="col" 
                    className="px-6 py-4 text-sm font-bold text-[#1e2c49] font-['Pretendard'] text-left"
                  >
                    {header.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#e5e8eb]">
              {users.map((user) => {
                const cells = [
                  {
                    id: 'name',
                    content: (
                      <div className={`text-base font-semibold font-['Pretendard'] ${
                        user.status === 'active' ? 'text-[#1e2c49]' : 'text-gray-500'
                      }`}>
                        {user.name}{user.status !== 'active' && <span className="ml-2 text-xs text-red-500">⚠️</span>}
                      </div>
                    )
                  },
                  {
                    id: 'email',
                    content: (
                      <div className={`text-base font-['Pretendard'] ${
                        user.status === 'active' ? 'text-[#6b7684]' : 'text-gray-400'
                      }`}>
                        {user.email}
                      </div>
                    )
                  },
                  {
                    id: 'organization',
                    content: (
                      <div className={`text-base font-['Pretendard'] ${
                        user.status === 'active' ? 'text-[#6b7684]' : 'text-gray-400'
                      }`}>
                        {user.organization}
                      </div>
                    )
                  },
                  {
                    id: 'phoneNumber',
                    content: (
                      <div className={`text-base font-['Pretendard'] ${
                        user.status === 'active' ? 'text-[#6b7684]' : 'text-gray-400'
                      }`}>
                        {user.phoneNumber}
                      </div>
                    )
                  },
                  {
                    id: 'role',
                    content: (
                      <div className={`text-base font-['Pretendard'] ${
                        user.status === 'active' ? 'text-[#6b7684]' : 'text-gray-400'
                      }`}>
                        {getRoleLabel(user.userLevel)}
                      </div>
                    )
                  },
                  {
                    id: 'status',
                    content: (
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium font-['Pretendard'] ${getStatusBadgeClass(user.status)}`}>
                        {getStatusText(user.status)}
                      </span>
                    )
                  },
                  {
                    id: 'createdAt',
                    content: (
                      <div className={`text-base font-['Pretendard'] ${
                        user.status === 'active' ? 'text-[#6b7684]' : 'text-gray-400'
                      }`}>
                        {formatDate(user.createdAt)}
                      </div>
                    )
                  },
                  {
                    id: 'lastLoginAt',
                    content: (
                      <div className={`text-base font-['Pretendard'] ${
                        user.status === 'active' ? 'text-[#6b7684]' : 'text-gray-400'
                      }`}>
                        {formatLastLogin(user.lastLoginAt, user.createdAt)}
                      </div>
                    )
                  }
                ];

                return (
                  <tr 
                    key={user.uid} 
                    className={`cursor-pointer transition-colors ${
                      user.status === 'active' 
                        ? 'hover:bg-[#f8fafc]' 
                        : 'hover:bg-red-50 opacity-75'
                    }`}
                    onClick={() => handleUserClick(user)}
                  >
                    {cells.map((cell) => (
                      <td
                        key={`${user.uid}_${cell.id}`}
                        className="px-6 py-4 whitespace-nowrap"
                      >
                        {cell.content}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* 경고 모달 */}
      <WarningModal
        isOpen={showWarning}
        onClose={handleCloseWarning}
        userName={selectedUser?.name || ''}
        userStatus={selectedUser?.status || ''}
      />
    </React.Fragment>
  );
};

export default UserTable; 
